import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, fmtErr, fmtTs, secsAgo } from "../api";
import { Button, Card, CardContent, Badge } from "../components/ui";
import {
  CheckCircle2, XCircle, Wifi, WifiOff, RotateCcw, ExternalLink, Play,
  Music, Volume2, Zap, Clock, AlertTriangle, ShieldCheck, Radio,
} from "lucide-react";

const STATUS_COLORS = {
  PENDING: "bg-white/10 text-white/80",
  GENERATING: "bg-blue-500/20 text-blue-300",
  READY: "bg-amber-500/20 text-amber-300",
  QUEUED: "bg-purple-500/20 text-purple-300",
  PLAYING: "bg-[hsl(var(--racing-live))]/20 text-[hsl(var(--racing-live))]",
  PLAYED: "bg-[hsl(var(--racing-green))]/20 text-[hsl(var(--racing-green))]",
  FAILED: "bg-red-500/20 text-red-300",
  CANCELLED: "bg-white/5 text-gray-500",
  BLOCKED: "bg-orange-500/20 text-orange-300",
  SKIPPED_DELAY: "bg-white/5 text-gray-400 line-through",
};

const TYPE_LABELS = {
  FIRST_CALL: "20 min antes",
  SECOND_CALL: "10 min antes",
  FINAL_CALL: "Na largada",
  NEXT_SESSION_DELAY_CALL: "Chamada de atraso",
  MANUAL: "Manual",
  REPLAY: "Replay",
};

function useCountdown(iso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Math.round((t - now) / 1000);
}

function fmtCountdown(secs) {
  if (secs === null || secs === undefined) return "—";
  if (secs < 0) return "AGORA";
  const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = secs % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}min ${String(s).padStart(2, "0")}s`;
  return `${s}s`;
}

export default function Dashboard() {
  const [status, setStatus] = useState(null);
  const [upcoming, setUpcoming] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyTest, setBusyTest] = useState(false);
  const audioRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const load = async () => {
    try {
      const [s, u] = await Promise.all([api.get("/status"), api.get("/upcoming-call")]);
      setStatus(s.data); setUpcoming(u.data || null);
    } catch { }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); const id = setInterval(load, 3000); return () => clearInterval(id); }, []);

  const previewAnnouncement = (a) => {
    if (!a.audio_cache_key) return toast.error("Áudio ainda não gerado");
    const base = (process.env.REACT_APP_API_BASE || "/api/audio").replace(/\/$/, "");
    const url = `${base}/tts/${a.audio_cache_key}.${a.audio_format || "mp3"}`;
    setPreviewUrl(url);
    setTimeout(() => {
      if (audioRef.current) { audioRef.current.src = url; audioRef.current.play().catch(() => {}); }
    }, 50);
  };

  const testAutomatic = async (type) => {
    setBusyTest(true);
    try {
      const r = await api.post("/announcements/test-auto", { announcement_type: type });
      if (r.data.delivered) toast.success(`Chamada de teste enviada — agente ${r.data.agent_name}`);
      else toast.warning(`Áudio gerado, mas não entregue: ${r.data.reason}`);
      await load();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusyTest(false); }
  };

  const changeMode = async (nextMode) => {
    if (nextMode === "PRODUCTION") {
      const ok = window.confirm(
        "ATIVAR CHAMADAS AUTOMÁTICAS?\n\n" +
        "O sistema passará a emitir chamadas reais nas caixas de som do KGV.\n\n" +
        "Confirma?"
      );
      if (!ok) return;
    }
    try {
      await api.post("/mode", { mode: nextMode, confirm: nextMode === "PRODUCTION" });
      toast.success(`Modo alterado para ${nextMode}`);
      await load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const countdown = useCountdown(upcoming?.scheduled_at);

  if (loading) return <div className="p-6 text-gray-500">Carregando...</div>;

  const source = status?.source || {};
  const settings = status?.settings || {};
  const session = status?.session;
  const announcements = status?.announcements || [];
  const agents = status?.agents || [];
  const poller = status?.poller || {};
  const delayed = status?.delayed || [];
  const blocked = status?.blocked || [];
  const logs = status?.logs || [];
  const currentState = status?.current_session_state || { active: false };
  const mode = status?.mode || "DISABLED";
  const onlineAgents = agents.filter((a) => a.online);
  const agent = onlineAgents[0] || null;
  const spotify = agent?.spotify_status;
  const nextMonitor = poller?.next_session || {};
  const currMonitor = poller?.current_session || {};

  const MODE_STYLES = {
    DISABLED: { dot: "bg-gray-500", label: "SISTEMA DESATIVADO", ring: "ring-gray-500/40", text: "text-gray-400" },
    TEST:     { dot: "bg-amber-400 animate-pulse2", label: "MODO TESTE", ring: "ring-amber-500/40", text: "text-amber-400" },
    PRODUCTION: { dot: "bg-green-400 animate-pulse2", label: "PRODUÇÃO ATIVA", ring: "ring-green-500/40", text: "text-green-400" },
  };
  const ms = MODE_STYLES[mode];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--primary))]">{source.name}</div>
          <h1 className="font-display text-4xl sm:text-5xl uppercase leading-none mt-2">Dashboard</h1>
          <div className="text-sm text-gray-500 mt-2">
            Polling a cada {settings.polling_interval_seconds}s • Fuso {settings.timezone}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" data-testid="test-first-call-btn" onClick={() => testAutomatic("FIRST_CALL")} disabled={busyTest || !session}>
            <Zap size={12} className="mr-1" /> Testar 20 min
          </Button>
          <Button variant="outline" size="sm" data-testid="test-second-call-btn" onClick={() => testAutomatic("SECOND_CALL")} disabled={busyTest || !session}>
            <Zap size={12} className="mr-1" /> Testar 10 min
          </Button>
        </div>
      </header>

      {/* Fase 6: MODE BANNER */}
      <div className={`border p-4 rounded-sm flex flex-col md:flex-row items-start md:items-center gap-4 ring-1 ${ms.ring} bg-panel border-panel`} data-testid="mode-banner">
        <div className="flex items-center gap-3 flex-1">
          <span className={`inline-block w-3 h-3 rounded-full ${ms.dot}`} />
          <div>
            <div className={`font-display text-2xl uppercase tracking-tight ${ms.text}`}>{ms.label}</div>
            <div className="text-xs text-gray-500 mt-0.5">
              {mode === "DISABLED" && "Nenhuma chamada será executada. Ative TESTE para simular sem áudio ou PRODUÇÃO para emitir chamadas reais."}
              {mode === "TEST" && "Chamadas são criadas e visíveis, mas nenhum áudio é gerado ou reproduzido."}
              {mode === "PRODUCTION" && "Chamadas automáticas ativas. Anúncios reais serão reproduzidos nas caixas de som do KGV."}
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant={mode === "DISABLED" ? "primary" : "outline"} size="sm" onClick={() => changeMode("DISABLED")} data-testid="mode-disabled-btn">
            Desativar
          </Button>
          <Button variant={mode === "TEST" ? "primary" : "outline"} size="sm" onClick={() => changeMode("TEST")} data-testid="mode-test-btn">
            Modo Teste
          </Button>
          <Button variant={mode === "PRODUCTION" ? "primary" : "outline"} size="sm" onClick={() => changeMode("PRODUCTION")} data-testid="mode-production-btn">
            <ShieldCheck size={12} className="mr-1" /> Ativar Produção
          </Button>
        </div>
      </div>

      {/* Delayed alert */}
      {delayed.length > 0 && (
        <div className="border border-amber-500/40 bg-amber-500/5 p-3 flex items-center gap-3" data-testid="delayed-banner">
          <AlertTriangle size={16} className="text-amber-400" />
          <div className="text-sm text-amber-300">
            <strong>{delayed.length}</strong> chamada(s) atrasada(s) — agente possivelmente offline. Serão entregues assim que o agente reconectar.
          </div>
        </div>
      )}

      {/* Fase 7 — Blocked alert */}
      {blocked.length > 0 && (
        <div className="border border-orange-500/40 bg-orange-500/5 p-3 flex items-center gap-3" data-testid="blocked-banner">
          <ShieldCheck size={16} className="text-orange-400" />
          <div className="text-sm text-orange-300 flex-1">
            <strong>{blocked.length}</strong> chamada(s) bloqueada(s) — sessão atual em andamento
            {currentState.title ? <span className="opacity-80"> ({currentState.title})</span> : null}
            . Nada será anunciado até a sessão atual terminar.
          </div>
        </div>
      )}

      {/* Fase 9.2 — Current session monitor state + DELAY MODE */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className={`border p-4 rounded-sm ${currentState.delay_mode ? "border-red-500/60 bg-red-500/10 animate-pulse2" : (currentState.active ? "border-[hsl(var(--racing-live))]/40 bg-[hsl(var(--racing-live))]/5" : "border-panel bg-panel")}`} data-testid="current-session-card">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-widest text-gray-500 flex items-center gap-1"><Radio size={12}/> Sessão Atual</div>
            <span className={`text-[10px] uppercase tracking-widest font-semibold ${currentState.delay_mode ? "text-red-400" : (currentState.active ? "text-[hsl(var(--racing-live))]" : "text-gray-500")}`} data-testid="session-status-badge">
              {currentState.delay_mode ? "🔴 ATRASO DETECTADO" : (currentState.active ? "🟢 NO HORÁRIO" : "⚪ NENHUMA")}
            </span>
          </div>
          <div className="font-display text-2xl uppercase leading-tight">
            {currentState.active ? (currentState.title || "Sessão sem título") : "—"}
          </div>
          <div className="text-xs text-gray-500 mt-1 space-y-0.5">
            {currentState.active ? (
              <>
                <div>tempo restante: <strong>{currentState.time_left || "—"}</strong> · {currentState.pilot_count} piloto(s) na pista</div>
                {currentState.session_end_expected ? (
                  <div>término previsto: <strong>{new Date(currentState.session_end_expected + "Z").toLocaleTimeString('pt-BR', { timeZone: "America/Sao_Paulo", hour: '2-digit', minute: '2-digit' })}</strong></div>
                ) : null}
                {currentState.delay_mode ? (
                  <div className="text-red-400 font-semibold">⚠ Atraso: +{Math.floor((currentState.delay_seconds || 0) / 60)} min {(currentState.delay_seconds || 0) % 60}s · modo DELAY ativo</div>
                ) : null}
              </>
            ) : (
              "Nenhuma sessão em execução — chamadas seguem o relógio da programação."
            )}
          </div>
        </div>
        <div className="border border-panel bg-panel p-4 rounded-sm" data-testid="monitors-status-card">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">Monitores RaceFacer</div>
          <div className="space-y-2 text-xs">
            <MonitorRow label="next-sess" tel={nextMonitor} testId="next-sess-status" />
            <MonitorRow label="current-session-monitor" tel={currMonitor} testId="current-sess-status" />
          </div>
        </div>
      </div>

      {/* Status bar — 4 cartões */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <StatusCard
          title="Próxima Chamada"
          icon={<Clock size={14} />}
          value={upcoming ? TYPE_LABELS[upcoming.announcement_type] || upcoming.announcement_type : "—"}
          badge={upcoming ? <Badge className={STATUS_COLORS[upcoming.status] || "bg-white/10"}>{upcoming.status}</Badge> : null}
          sub={upcoming ? `em ${fmtCountdown(countdown)}` : "Nenhuma agendada"}
          testId="card-upcoming"
        />
        <StatusCard
          title="Audio Agent"
          icon={<Wifi size={14} />}
          value={agent ? agent.name : "—"}
          badge={<Badge className={onlineAgents.length > 0 ? "bg-green-500/20 text-green-300" : "bg-red-500/20 text-red-300"}>
            {onlineAgents.length > 0 ? "ONLINE" : "OFFLINE"}
          </Badge>}
          sub={agent ? `${agent.hostname || "?"} · ${agent.state}` : "Nenhum conectado"}
          testId="card-agent"
        />
        <StatusCard
          title="Spotify"
          icon={<Music size={14} />}
          value={spotify?.svcl_available ? (spotify?.spotify_running ? "TOCANDO" : "PARADO") : "N/A"}
          badge={<Badge className={
            !agent ? "bg-white/5 text-gray-500"
            : !spotify?.svcl_available ? "bg-white/5 text-gray-500"
            : spotify?.spotify_running ? "bg-green-500/20 text-green-300"
            : "bg-amber-500/20 text-amber-300"
          }>
            {!agent ? "—" : !spotify?.svcl_available ? "svcl AUSENTE" : spotify?.spotify_running ? "DETECTADO" : "NÃO DETECTADO"}
          </Badge>}
          sub={spotify?.svcl_available ? "Controle disponível" : "Instale svcl.exe no agente"}
          testId="card-spotify"
        />
        <StatusCard
          title="Ducking"
          icon={<Volume2 size={14} />}
          value={spotify?.ducking_enabled ? `${spotify?.duck_target_percent}%` : "OFF"}
          badge={<Badge className={
            spotify?.ducking_enabled ? "bg-[hsl(var(--primary))]/20 text-[hsl(var(--primary))]" : "bg-white/5 text-gray-500"
          }>
            {spotify?.ducking_enabled ? "ATIVADO" : "DESATIVADO"}
          </Badge>}
          sub={spotify?.ducking_enabled ? `fade ${spotify?.fade_out_ms}/${spotify?.fade_in_ms}ms` : "Configure no agente"}
          testId="card-ducking"
        />
      </div>

      {/* Próxima Sessão + Fonte */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-[hsl(var(--primary))] animate-pulse2" />
                <div className="font-display text-xl uppercase tracking-tight">Próxima Sessão</div>
              </div>
              {poller.last_polled_at && (
                <span className="text-[10px] uppercase tracking-widest text-gray-500">
                  última: {secsAgo(poller.last_polled_at)}
                </span>
              )}
            </div>
            {!session ? (
              <div className="py-12 text-center text-gray-500">Aguardando primeira sessão…</div>
            ) : (
              <div className="space-y-4" data-testid="current-session">
                <div className="flex items-baseline gap-4 flex-wrap">
                  <div className="font-display text-4xl uppercase leading-none">{session.name}</div>
                  <Badge className="border border-panel">#{session.session_id}</Badge>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <StatBox label="Início" value={session.start_time || "—"} />
                  <StatBox label="Check-in" value={session.checkin_time || "—"} />
                  <StatBox label="Pilotos" value={(session.pilots || []).length} accent />
                  <StatBox label="Pista" value={session.track_configuration || "—"} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">Pilotos ({(session.pilots||[]).length})</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                    {(session.pilots || []).map((p, i) => (
                      <div key={p.uuid || i} className="flex items-center gap-2 p-2 border border-panel text-sm" data-testid={`pilot-${i}`}>
                        <div className="w-2 h-8 rounded-sm" style={{ backgroundColor: p.kart_color ? `#${p.kart_color}` : "#333" }} />
                        <div className="flex-1 truncate">{p.display_name}</div>
                        <span className="text-xs text-gray-500">{p.kart_name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="font-display text-xl uppercase tracking-tight mb-4">Fonte de Dados</div>
            <div className="space-y-3 text-sm">
              <Row label="URL" value={<a href={poller.url} target="_blank" rel="noreferrer" className="text-[hsl(var(--primary))] hover:underline break-all">{poller.url}<ExternalLink size={10} className="inline ml-1" /></a>} />
              <Row label="Status" value={poller.last_error ? <span className="text-red-400">{poller.last_error}</span> : <span className="text-green-400">200 OK</span>} />
              <Row label="Última consulta" value={fmtTs(poller.last_polled_at)} />
              <Row label="Último sucesso" value={fmtTs(poller.last_success_at)} />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display text-xl uppercase tracking-tight">Chamadas Recentes</div>
            <Button
              data-testid="replay-last-btn" variant="outline" size="sm"
              onClick={async () => { try { await api.post("/announcements/replay-last"); toast.success("Reproduzindo última chamada"); } catch (e) { toast.error(fmtErr(e)); } }}
            >
              <RotateCcw size={12} className="mr-1" /> Repetir última
            </Button>
          </div>
          {announcements.length === 0 ? (
            <div className="py-8 text-center text-gray-500 text-sm">Nenhuma chamada ainda.</div>
          ) : (
            <div className="space-y-1">
              {announcements.map((a) => (
                <div key={a.id} className="flex items-center gap-3 p-3 border border-panel text-sm" data-testid={`ann-${a.id}`}>
                  <Badge className={STATUS_COLORS[a.status] || "bg-white/5"}>{a.status}</Badge>
                  <div className="w-24 uppercase text-[10px] tracking-widest text-gray-500">{TYPE_LABELS[a.announcement_type] || a.announcement_type}</div>
                  <div className="w-20 font-mono-data text-xs text-gray-500">#{a.session_id?.slice(0, 12)}</div>
                  <div className="flex-1 truncate text-gray-400">{a.message || "—"}</div>
                  <div className="text-xs text-gray-500">{fmtTs(a.scheduled_at)}</div>
                  {a.audio_cache_key && (
                    <button title="Ouvir no navegador" className="text-gray-400 hover:text-white p-1"
                      onClick={() => previewAnnouncement(a)} data-testid={`preview-btn-${a.id}`}>
                      <Play size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {previewUrl && (
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--primary))]">Preview no navegador</div>
            <audio ref={audioRef} controls src={previewUrl} className="flex-1" data-testid="dashboard-preview-player" />
            <button className="text-gray-400 hover:text-white text-xs" onClick={() => setPreviewUrl(null)}>fechar</button>
          </CardContent>
        </Card>
      )}

      {/* Fase 7 — event log */}
      <Card>
        <CardContent className="p-6">
          <div className="font-display text-xl uppercase tracking-tight mb-4">Log do Scheduler</div>
          {logs.length === 0 ? (
            <div className="py-6 text-center text-gray-500 text-sm">Nenhum evento registrado ainda.</div>
          ) : (
            <div className="space-y-1 font-mono-data text-xs" data-testid="event-log">
              {logs.map((l) => (
                <div key={l.id} className="flex items-start gap-3 border-b border-panel/50 py-1.5">
                  <span className="text-gray-500 w-32 shrink-0">{fmtTs(l.created_at)}</span>
                  <EventBadge event={l.event} />
                  <span className="text-gray-400 flex-1 truncate">{formatEventMeta(l.event, l.meta)}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function MonitorRow({ label, tel, testId }) {
  const online = tel?.last_status === 200 && !tel?.last_error;
  const lastSec = tel?.last_polled_at ? Math.round(Date.now() / 1000 - tel.last_polled_at) : null;
  return (
    <div className="flex items-center gap-3" data-testid={testId}>
      <span className={`inline-block w-2 h-2 rounded-full ${online ? "bg-green-400 animate-pulse2" : "bg-red-400"}`} />
      <span className="uppercase tracking-widest text-[10px] w-56 truncate">{label}</span>
      <span className={online ? "text-green-400" : "text-red-400"}>
        {online ? "ONLINE" : (tel?.last_error || "OFFLINE")}
      </span>
      <span className="text-gray-500 ml-auto">
        {lastSec !== null ? `${lastSec}s atrás` : "—"}
      </span>
    </div>
  );
}

const EVENT_STYLES = {
  CURRENT_SESSION_ACTIVE: "bg-[hsl(var(--racing-live))]/20 text-[hsl(var(--racing-live))]",
  CURRENT_SESSION_FINISHED: "bg-green-500/20 text-green-300",
  CURRENT_SESSION_ROLLOVER: "bg-blue-500/20 text-blue-300",
  NEXT_SESSION_DETECTED: "bg-white/10 text-white/70",
  ANNOUNCEMENT_BLOCKED: "bg-orange-500/20 text-orange-300",
  ANNOUNCEMENT_RELEASED: "bg-cyan-500/20 text-cyan-300",
  ANNOUNCEMENT_PLAYED: "bg-[hsl(var(--racing-green))]/20 text-[hsl(var(--racing-green))]",
  ANNOUNCEMENT_FAILED: "bg-red-500/20 text-red-300",
  ANNOUNCEMENT_EXPIRED: "bg-gray-500/20 text-gray-400",
  DELAY_DETECTED: "bg-red-500/30 text-red-200 font-semibold",
  DELAY_MODE_ENABLED: "bg-red-500/20 text-red-300",
  DELAY_MODE_DISABLED: "bg-green-500/20 text-green-300",
  NEXT_SESSION_DELAY_CALL_CREATED: "bg-purple-500/20 text-purple-300",
  NEXT_SESSION_DELAY_CALL_PLAYED: "bg-purple-500/30 text-purple-200 font-semibold",
};

function EventBadge({ event }) {
  const cls = EVENT_STYLES[event] || "bg-white/10 text-white/70";
  return <span className={`px-2 py-0.5 text-[9px] uppercase tracking-widest rounded-sm w-56 shrink-0 text-center ${cls}`}>{event}</span>;
}

function formatEventMeta(event, meta) {
  if (!meta) return "";
  if (event === "CURRENT_SESSION_ACTIVE") return `Iniciou: ${meta.title || "?"} (${meta.pilot_count || 0} pilotos) · fim previsto: ${meta.end_expected ? new Date(meta.end_expected + "Z").toLocaleTimeString('pt-BR', { timeZone: "America/Sao_Paulo" }) : "?"}`;
  if (event === "CURRENT_SESSION_FINISHED") return `Terminou: ${meta.prev_title || "?"}${meta.was_delay_mode ? " (estava em ATRASO)" : ""}`;
  if (event === "CURRENT_SESSION_ROLLOVER") return `${meta.from_title || "?"} → ${meta.to_title || "?"}`;
  if (event === "NEXT_SESSION_DETECTED") return `#${meta.session_id} ${meta.name || ""} @ ${meta.start_time || "?"} (${meta.pilot_count || 0} pilotos)`;
  if (event === "ANNOUNCEMENT_BLOCKED") return `${meta.type || "?"} bloqueado — sessão em andamento: ${meta.current_session_title || "?"}`;
  if (event === "ANNOUNCEMENT_RELEASED") return `${meta.type || "?"} liberado — sessão anterior terminou`;
  if (event === "ANNOUNCEMENT_PLAYED") return `${meta.type || "?"} tocado no agente ${meta.agent_name || "?"}`;
  if (event === "ANNOUNCEMENT_FAILED") return `${meta.type || "?"} falhou: ${meta.error || "?"}`;
  if (event === "ANNOUNCEMENT_EXPIRED") return `${meta.type || "?"} expirou (${meta.reason || "past_window"})`;
  if (event === "DELAY_DETECTED") return `${meta.title || "?"} atrasou +${Math.round((meta.delay_seconds || 0) / 60)}min · término previsto: ${meta.scheduled_end ? new Date(meta.scheduled_end + "Z").toLocaleTimeString('pt-BR', { timeZone: "America/Sao_Paulo" }) : "?"}`;
  if (event === "DELAY_MODE_ENABLED") return `Modo ATRASO ativado — ${meta.title || "?"}`;
  if (event === "DELAY_MODE_DISABLED") return `Modo ATRASO desativado — ${meta.prev_title || "?"} terminou`;
  if (event === "NEXT_SESSION_DELAY_CALL_CREATED") return `Chamada de atraso criada · sessão ${meta.session_id} · ${meta.pilot_count || 0} pilotos`;
  if (event === "NEXT_SESSION_DELAY_CALL_PLAYED") return `Chamada de atraso tocada no agente ${meta.agent_name || "?"} · sessão ${meta.session_id}`;
  return JSON.stringify(meta);
}

function StatusCard({ title, icon, value, badge, sub, testId }) {
  return (
    <div className="bg-panel border border-panel p-4" data-testid={testId}>
      <div className="flex items-center justify-between gap-2 text-[10px] uppercase tracking-widest text-gray-500 mb-2">
        <span className="flex items-center gap-1">{icon}{title}</span>
        {badge}
      </div>
      <div className="font-display text-2xl uppercase leading-tight text-white mb-1">{value}</div>
      <div className="text-xs text-gray-500 truncate">{sub}</div>
    </div>
  );
}

function StatBox({ label, value, accent }) {
  return (
    <div className={`border p-3 ${accent ? "border-[hsl(var(--primary))]/40" : "border-panel"}`}>
      <div className="text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
      <div className={`font-display text-2xl mt-1 ${accent ? "text-[hsl(var(--primary))]" : "text-white"}`}>{value}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-2 border-b border-panel pb-2 last:border-0">
      <div className="text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
      <div className="text-right text-xs max-w-[70%] break-all">{value}</div>
    </div>
  );
}
